import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';
import InventoryTable from './components/InventoryTable';
import InventoryDrawers from './components/InventoryDrawers';
import InventoryHeader from './components/InventoryHeader';
import InventorySummaryCards from './components/InventorySummaryCards';
import InventoryToolbar from './components/InventoryToolbar';
import { useToken } from '@/common/helper';
import {
  isPreviewTabActive,
  PREVIEW_INVENTORY_VIEW_MODE_KEY,
} from '@/common/helper/tokenStorage';
import { deviceSortOptions, skuSortOptions } from '@/common/static_data';
import type {
  AppliedFilter,
  FilterArrayItem,
  ShipmentFiltersPayload,
} from '../Shipments/ControlTower/types';
import type {
  InventoryApiItem,
  InventoryApiPagination,
  InventoryApiResponse,
  InventoryGroupField,
  InventorySortKey,
  InventorySortDirection,
  InventoryUnifiedResponse,
  InventoryViewMode,
  LoadGroupItemsResponse,
  LoadSubgroupItemsResponse,
  LoadSubgroupsResponse,
} from './type';
import {
  DownloadInventoryList,
  GetInventoryList,
  GetInventoryRefreshStatus,
  TriggerInventoryRefresh,
} from '@/apis/inventory';
import { saveBlobAsFile } from '@/pages/Shipments/components/fileDownload';
import { buildShipmentFiltersPayload } from '@/pages/Shipments/ControlTower/utils/buildShipmentFiltersPayload';
import {
  buildInventoryUnifiedResponse,
  DEFAULT_INVENTORY_VIEW,
  dedupeGroups,
  dedupeItems,
  dedupeSubgroups,
  EMPTY_INVENTORY_RESPONSE,
  INVENTORY_SORT_DEFAULTS,
  ITEM_PAGE_SIZE,
  mapGroup,
  mapGroupWithSubgroups,
  mapInventoryItem,
  mapInventoryViewToApi,
  mapPagination,
  mapSubgroup,
  PAGE_SIZE,
  shouldIncludeItemForView,
  SUBGROUPS_PAGE_SIZE,
} from './utils/inventoryResponseMappers';
import { PER_DEVICE_HIDDEN_FILTER_KEYS } from './utils/inventoryFiltersConfig';

type InventoryPageProps = {
  title: string;
};

type InventoryRefreshStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';

type InventoryRefreshStatusPayload = {
  task_id?: string;
  status?: InventoryRefreshStatus;
  progress?: number;
  last_refresh_time?: string | null;
  message?: string;
};

const getInventoryRefreshData = (
  response: unknown
): InventoryRefreshStatusPayload | null => {
  const data = response as {
    data?: {
      data?: {
        data?: InventoryRefreshStatusPayload;
        message?: string;
        task_id?: string;
        status?: InventoryRefreshStatus;
      };
    };
  };

  const refreshData = data?.data?.data?.data;
  if (!refreshData) return null;

  return {
    ...refreshData,
    message: refreshData.message ?? data?.data?.data?.message,
  };
};

const parseInventoryLastRefreshTime = (value?: string | null) => {
  if (!value) return Date.now();

  const parsedTime = new Date(value).getTime();
  return Number.isNaN(parsedTime) ? Date.now() : parsedTime;
};

export type InventoryEditState = {
  id?: string;
  isDuplicate?: boolean;
};

const getInventoryViewStorageKey = (): string => {
  return isPreviewTabActive()
    ? PREVIEW_INVENTORY_VIEW_MODE_KEY
    : 'inventory_view_mode';
};

const getSavedInventoryView = (): InventoryViewMode => {
  if (typeof window === 'undefined') return DEFAULT_INVENTORY_VIEW;

  const saved = window.sessionStorage.getItem(getInventoryViewStorageKey());
  if (saved === 'per_sku' || saved === 'sku') return 'per_sku';
  if (saved === 'per_device' || saved === 'device') return 'per_device';

  return DEFAULT_INVENTORY_VIEW;
};

const InventoryPage: React.FC<InventoryPageProps> = ({ title }) => {
  const { token } = useToken();
  const location = useLocation();

  const [showDetailDrawer, setShowDetailDrawer] = useState<boolean>(false);
  const [deepLinkedInventoryId, setDeepLinkedInventoryId] = useState<
    string | null
  >(null);
  const [, setShowDrawer] = useState(false);
  const [editID, setEditID] = useState<InventoryEditState>({});
  const [inventoryResponse, setInventoryResponse] =
    useState<InventoryUnifiedResponse>(EMPTY_INVENTORY_RESPONSE);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
  const [filterArray, setFilterArray] = useState<FilterArrayItem[]>([]);
  const [filtersPayload, setFiltersPayload] =
    useState<ShipmentFiltersPayload | null>(null);

  // Stash for the 5 Per-SKU-only filters while the user is in Per Device view.
  // Restored automatically when the user switches back to Per SKU.
  type SkuFilterStash = {
    appliedFilters: AppliedFilter[];
    filterArray: FilterArrayItem[];
  };
  const [skuFilterStash, setSkuFilterStash] = useState<SkuFilterStash | null>(
    null
  );
  const [groupBy, setGroupBy] = useState<InventoryGroupField>(null);
  const [subGroupBy, setSubGroupBy] = useState<InventoryGroupField>(null);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const dropdownMaxHeight = 'calc(100vh - 400px)';
  const [inventoryView, setInventoryView] = useState<InventoryViewMode>(
    getSavedInventoryView
  );
  const [skuSortKey, setSkuSortKey] = useState<InventorySortKey>(
    INVENTORY_SORT_DEFAULTS.per_sku
  );
  const [skuSortDirection, setSkuSortDirection] =
    useState<InventorySortDirection>('asc');
  const [deviceSortKey, setDeviceSortKey] = useState<InventorySortKey>(
    INVENTORY_SORT_DEFAULTS.per_device
  );
  const [deviceSortDirection, setDeviceSortDirection] =
    useState<InventorySortDirection>('asc');
  const [lastRefreshTime, setLastRefreshTime] = useState<number>(Date.now());
  const [freshnessText, setFreshnessText] = useState<string>('Just now');
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const lastRequestSignatureRef = useRef<string>('');
  const activeScopeRef = useRef<string>('');
  const refreshPollingTimeoutRef = useRef<number | null>(null);
  const isPerDeviceView = inventoryView === 'per_device';
  const sortKey = isPerDeviceView ? deviceSortKey : skuSortKey;
  const sortDirection = isPerDeviceView
    ? deviceSortDirection
    : skuSortDirection;
  const sortOptions = isPerDeviceView ? deviceSortOptions : skuSortOptions;

  const getFreshnessText = useCallback((timestamp: number) => {
    const diffSec = Math.floor((Date.now() - timestamp) / 1000);
    if (diffSec < 60) return 'Just now';

    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;

    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour} hr ago`;

    const diffDay = Math.floor(diffHour / 24);
    return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  }, []);

  useEffect(() => {
    setFreshnessText(getFreshnessText(lastRefreshTime));
    const interval = window.setInterval(() => {
      setFreshnessText(getFreshnessText(lastRefreshTime));
    }, 60000);

    return () => window.clearInterval(interval);
  }, [getFreshnessText, lastRefreshTime]);

  useEffect(
    () => () => {
      if (refreshPollingTimeoutRef.current) {
        window.clearTimeout(refreshPollingTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inventoryId = (params.get('inventory_id') || '')
      .trim()
      .replace(/^\/+|\/+$/g, '');

    if (inventoryId) {
      setDeepLinkedInventoryId(inventoryId);
      setShowDetailDrawer(true);
    }
  }, [location.search]);

  const clearInventoryDeepLinkFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('inventory_id')) return;

    params.delete('inventory_id');
    const nextSearch = params.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;

    window.history.replaceState(window.history.state, '', nextUrl);
  }, [location.pathname, location.search]);

  const mappedFiltersPayload = useMemo(() => {
    const raw = filtersPayload ?? {};

    return Object.fromEntries(
      Object.entries(raw).map(([key, value]) => {
        if (key === 'locations') return ['location_ids', value];
        if (key === 'team') return ['team_ids', value];
        if (key === 'record_type') return ['record_types', value];

        return [key, value];
      })
    );
  }, [filtersPayload]);

  const mode = useMemo(() => {
    if (groupBy && subGroupBy) return 'group_subgroup';
    if (groupBy) return 'group';
    return 'flat';
  }, [groupBy, subGroupBy]);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        mode,
        search: debouncedSearch,
        filters: mappedFiltersPayload ?? {},
        groupBy: groupBy ?? '',
        subGroupBy: subGroupBy ?? '',
        view: inventoryView,
        sortBy: sortKey,
        sortDirection,
      }),
    [
      mode,
      debouncedSearch,
      mappedFiltersPayload,
      groupBy,
      subGroupBy,
      inventoryView,
      sortKey,
      sortDirection,
    ]
  );

  const requestPayload = useMemo(() => {
    const action = mode === 'flat' ? 'load_items' : 'load_groups';

    const mapGroupValue = (val?: string | null) => {
      if (!val) return '';
      if (val === 'kit') return 'kits_items';
      return val;
    };

    return {
      mode,
      action,
      page,
      page_size: PAGE_SIZE,
      items_page_size: ITEM_PAGE_SIZE,
      subgroups_page_size: SUBGROUPS_PAGE_SIZE,
      search: debouncedSearch,
      filters: mappedFiltersPayload ?? {},
      view: mapInventoryViewToApi(inventoryView),
      sort_by: sortKey,
      sort_direction: sortDirection,
      group_by: mapGroupValue(groupBy),
      sub_group_by: mapGroupValue(subGroupBy),
    };
  }, [
    mode,
    page,
    debouncedSearch,
    mappedFiltersPayload,
    inventoryView,
    sortKey,
    sortDirection,
    groupBy,
    subGroupBy,
  ]);

  const applyResponseState = useCallback((result: InventoryUnifiedResponse) => {
    setInventoryResponse(result);
    setTotalPages(result.pagination.totalPages);
    setTotalCount(result.pagination.totalCount);
  }, []);

  useEffect(() => {
    activeScopeRef.current = scopeKey;
    setPage(1);
    setInventoryResponse(EMPTY_INVENTORY_RESPONSE);
    setTotalPages(1);
    setTotalCount(0);
  }, [scopeKey]);

  const fetchInventory = useCallback(
    async (payload: Record<string, unknown>, requestScope: string) => {
      setIsLoading(true);

      try {
        const response = await GetInventoryList(token, payload);

        if (activeScopeRef.current !== requestScope) {
          return;
        }

        const apiData = response?.data?.data?.data;

        if (apiData) {
          const normalized = buildInventoryUnifiedResponse(
            apiData as InventoryApiResponse,
            inventoryView
          );
          applyResponseState(normalized);
          setLastRefreshTime(
            parseInventoryLastRefreshTime(
              (apiData as InventoryApiResponse).last_refresh_time
            )
          );
        } else {
          applyResponseState(EMPTY_INVENTORY_RESPONSE);
        }
      } catch (error) {
        console.error('Failed to fetch inventory:', error);

        if (activeScopeRef.current === requestScope) {
          applyResponseState(EMPTY_INVENTORY_RESPONSE);
        }
      } finally {
        if (activeScopeRef.current === requestScope) {
          setIsLoading(false);
        }
      }
    },
    [applyResponseState, inventoryView, token]
  );

  const handleManualRefresh = useCallback(async () => {
    if (refreshPollingTimeoutRef.current) {
      window.clearTimeout(refreshPollingTimeoutRef.current);
      refreshPollingTimeoutRef.current = null;
    }

    setIsManualRefreshing(true);

    const loadLatestInventory = async (lastRefreshTime?: string | null) => {
      const response = await GetInventoryList(token, requestPayload);
      const apiData = response?.data?.data?.data;

      if (apiData) {
        const normalized = buildInventoryUnifiedResponse(
          apiData as InventoryApiResponse,
          inventoryView
        );
        applyResponseState(normalized);
        setLastRefreshTime(
          parseInventoryLastRefreshTime(
            (apiData as InventoryApiResponse).last_refresh_time ??
              lastRefreshTime
          )
        );
        return;
      }

      applyResponseState(EMPTY_INVENTORY_RESPONSE);
      throw new Error('Empty inventory response');
    };

    const pollRefreshStatus = async (taskId: string, attempt = 0) => {
      try {
        const response = await GetInventoryRefreshStatus(token, taskId);
        const refreshData = getInventoryRefreshData(response);
        const status = refreshData?.status;

        if (status === 'COMPLETED') {
          await loadLatestInventory(refreshData?.last_refresh_time);
          message.success('Data refresh completed');
          setIsManualRefreshing(false);
          return;
        }

        if (status === 'FAILED') {
          message.error(refreshData?.message || 'Failed to refresh data');
          setIsManualRefreshing(false);
          return;
        }

        if (attempt >= 60) {
          message.error('Inventory refresh is taking longer than expected');
          setIsManualRefreshing(false);
          return;
        }

        refreshPollingTimeoutRef.current = window.setTimeout(() => {
          void pollRefreshStatus(taskId, attempt + 1);
        }, 5000);
      } catch (error) {
        console.error('Inventory refresh status failed:', error);
        message.error('Failed to refresh data');
        setIsManualRefreshing(false);
      }
    };

    try {
      const response = await TriggerInventoryRefresh(token);
      const refreshData = getInventoryRefreshData(response);
      const taskId = refreshData?.task_id;

      if (!taskId) {
        throw new Error('Inventory refresh task id missing');
      }

      if (refreshData?.status === 'COMPLETED') {
        await loadLatestInventory(refreshData?.last_refresh_time);
        message.success('Data refresh completed');
        setIsManualRefreshing(false);
        return;
      }

      await pollRefreshStatus(taskId);
    } catch (error) {
      console.error('Manual refresh failed:', error);
      message.error('Failed to refresh data');
      setIsManualRefreshing(false);
    }
  }, [applyResponseState, inventoryView, requestPayload, token]);

  const handleSortChange = useCallback(
    (key: InventorySortKey, direction: InventorySortDirection) => {
      if (inventoryView === 'per_device') {
        setDeviceSortKey(key);
        setDeviceSortDirection(direction);
      } else {
        setSkuSortKey(key);
        setSkuSortDirection(direction);
      }

      setPage(1);
    },
    [inventoryView]
  );

  const handleInventoryViewChange = useCallback(
    (view: InventoryViewMode) => {
      setInventoryView(view);
      setPage(1);
      if (view === 'per_device') {
        // ── STASH: separate the 5 SKU-only filters from the rest ─────────────
        // We save them so they can be fully restored when returning to Per SKU,
        // rather than permanently losing them.
        const hiddenApplied = appliedFilters.filter((f) =>
          PER_DEVICE_HIDDEN_FILTER_KEYS.has(
            String(f.filterBy ?? f.dateFilter ?? '')
          )
        );
        const hiddenArray = filterArray.filter((f) =>
          PER_DEVICE_HIDDEN_FILTER_KEYS.has(String(f.filterBy ?? ''))
        );

        if (hiddenApplied.length > 0 || hiddenArray.length > 0) {
          setSkuFilterStash({
            appliedFilters: hiddenApplied,
            filterArray: hiddenArray,
          });
        }

        const nextApplied = appliedFilters.filter(
          (f) =>
            !PER_DEVICE_HIDDEN_FILTER_KEYS.has(
              String(f.filterBy ?? f.dateFilter ?? '')
            )
        );
        const nextArray = filterArray.filter(
          (f) => !PER_DEVICE_HIDDEN_FILTER_KEYS.has(String(f.filterBy ?? ''))
        );

        setAppliedFilters(nextApplied);
        setFilterArray(nextArray);
        // Rebuild payload cleanly from the remaining filters.
        setFiltersPayload(buildShipmentFiltersPayload(nextArray, nextApplied));
      } else {
        // ── RESTORE: merge stashed SKU-only filters back into active state ────
        if (skuFilterStash) {
          const nextApplied = [
            ...appliedFilters,
            ...skuFilterStash.appliedFilters,
          ];
          const nextArray = [...filterArray, ...skuFilterStash.filterArray];

          setAppliedFilters(nextApplied);
          setFilterArray(nextArray);
          // Rebuild payload with the fully restored filter set.
          setFiltersPayload(
            buildShipmentFiltersPayload(nextArray, nextApplied)
          );
          setSkuFilterStash(null);
        }
      }

      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(getInventoryViewStorageKey(), view);
      }
    },
    [appliedFilters, filterArray, skuFilterStash]
  );

  const handleInventoryFormSuccess = useCallback(async () => {
    await fetchInventory({ ...requestPayload, page: 1 }, scopeKey);
  }, [fetchInventory, requestPayload, scopeKey]);

  const handleDownloadInventory = useCallback(async () => {
    const {
      items_page_size: _itemsPageSize,
      subgroups_page_size: _subgroupsPageSize,
      ...downloadPayload
    } = requestPayload;

    try {
      setIsDownloading(true);
      const response = await DownloadInventoryList(token, {
        ...downloadPayload,
        action: 'download',
        file_type: 'xlsx',
      });
      saveBlobAsFile(
        response,
        `inventory-${mapInventoryViewToApi(inventoryView)}.xlsx`
      );
    } catch {
      message.error('Failed to download inventory report');
    } finally {
      setIsDownloading(false);
    }
  }, [inventoryView, requestPayload, token]);

  const handleDeleteSuccess = useCallback(async () => {
    setShowDetailDrawer(false);
    setDeepLinkedInventoryId(null);
    await fetchInventory({ ...requestPayload, page: 1 }, scopeKey);
  }, [fetchInventory, requestPayload, scopeKey]);

  useEffect(() => {
    const nextSignature = JSON.stringify(requestPayload);

    if (lastRequestSignatureRef.current === nextSignature) {
      return;
    }

    lastRequestSignatureRef.current = nextSignature;
    void fetchInventory(requestPayload, scopeKey);
  }, [requestPayload, scopeKey, fetchInventory]);

  const handleSearchIconClick = () => {
    setIsSearchVisible(true);
  };

  const handleCloseSearch = () => {
    setIsSearchVisible(false);
    setSearchValue('');
    setPage(1);
  };

  const handleSearchSubmit = (value?: string) => {
    const nextValue = typeof value === 'string' ? value : searchValue;
    setSearchValue(nextValue);
    setPage(1);
  };

  const handleRunSearchFromPanel = useCallback(
    ({
      query,
      page: nextPage,
      payload,
    }: {
      query: string;
      check: boolean;
      page?: number;
      pageSize?: number;
      calledFrom?: string;
      payload?: ShipmentFiltersPayload | null;
    }) => {
      const nextQuery = query ?? '';
      const nextPayload = payload ?? null;
      const resolvedPage = nextPage ?? 1;

      setSearchValue((prev) => (prev === nextQuery ? prev : nextQuery));

      setFiltersPayload((prev) => {
        const prevSignature = JSON.stringify(prev ?? {});
        const nextSignature = JSON.stringify(nextPayload ?? {});
        return prevSignature === nextSignature ? prev : nextPayload;
      });

      setPage((prev) => (prev === resolvedPage ? prev : resolvedPage));
    },
    []
  );

  const handleLoadMoreGroups = useCallback(
    async (nextPage: number) => {
      if (isLoading) return;

      const requestScope = scopeKey;
      const totalPagesCount = Number(
        inventoryResponse.pagination.totalPages ?? 1
      );
      const currentPage = Number(inventoryResponse.pagination.page ?? 1);

      if (currentPage >= totalPagesCount) return;

      const payload = {
        ...requestPayload,
        action: 'load_groups',
        page: nextPage,
      };

      setIsLoading(true);

      try {
        const response = await GetInventoryList(token, payload);

        if (activeScopeRef.current !== requestScope) {
          return;
        }

        const apiData = response?.data?.data?.data as
          | {
              pagination?: InventoryApiPagination;
              groups?: Array<{
                id: string;
                name: string;
                count: number;
                items?: InventoryApiItem[];
                pagination?: InventoryApiPagination;
                subgroups?: Array<{
                  id: string;
                  name: string;
                  color?: string | null;
                  count: number;
                  items?: InventoryApiItem[];
                  pagination?: InventoryApiPagination;
                }>;
              }>;
            }
          | undefined;

        if (!apiData) return;

        setInventoryResponse((prev) => {
          if (activeScopeRef.current !== requestScope) return prev;

          if (prev.mode === 'group') {
            return {
              ...prev,
              groups: dedupeGroups([
                ...prev.groups,
                ...(apiData.groups ?? []).map((group) =>
                  mapGroup(group, inventoryView)
                ),
              ]),
              pagination: mapPagination(apiData.pagination),
            };
          }

          if (prev.mode === 'group_subgroup') {
            return {
              ...prev,
              groups: dedupeGroups([
                ...prev.groups,
                ...(apiData.groups ?? []).map((group) =>
                  mapGroupWithSubgroups(group, inventoryView)
                ),
              ]),
              pagination: mapPagination(apiData.pagination),
            };
          }

          return prev;
        });

        const nextPagination = mapPagination(apiData.pagination);
        setTotalPages(nextPagination.totalPages);
        setTotalCount(nextPagination.totalCount);
      } catch (error) {
        console.error('Failed to load more groups:', error);
      } finally {
        if (activeScopeRef.current === requestScope) {
          setIsLoading(false);
        }
      }
    },
    [
      inventoryResponse.pagination,
      inventoryView,
      isLoading,
      requestPayload,
      scopeKey,
      token,
    ]
  );

  const handleLoadMoreSubgroups = useCallback(
    async (groupId: string, nextPage: number) => {
      if (isLoading) return;

      const requestScope = scopeKey;
      const payload = {
        ...requestPayload,
        action: 'load_subgroups',
        group_id: groupId,
        page: nextPage,
        page_size: SUBGROUPS_PAGE_SIZE,
      };

      setIsLoading(true);

      try {
        const response = await GetInventoryList(token, payload);

        if (activeScopeRef.current !== requestScope) {
          return;
        }

        const apiData = response?.data?.data?.data as
          LoadSubgroupsResponse | undefined;

        if (!apiData) return;

        setInventoryResponse((prev) => {
          if (activeScopeRef.current !== requestScope) return prev;
          if (prev.mode !== 'group_subgroup') return prev;

          const resolvedGroupId = apiData.groupId ?? apiData.group_id;
          if (resolvedGroupId !== groupId) return prev;

          return {
            ...prev,
            groups: prev.groups.map((group) => {
              if (group.id !== groupId) return group;

              return {
                ...group,
                subgroups: dedupeSubgroups([
                  ...group.subgroups,
                  ...(apiData.subgroups ?? []).map((subgroup) =>
                    mapSubgroup(subgroup, inventoryView)
                  ),
                ]),
                pagination: mapPagination(apiData.pagination),
              };
            }),
          };
        });
      } catch (error) {
        console.error('Failed to load more subgroups:', error);
      } finally {
        if (activeScopeRef.current === requestScope) {
          setIsLoading(false);
        }
      }
    },
    [inventoryView, isLoading, requestPayload, scopeKey, token]
  );

  const handleLoadMoreGroupItems = useCallback(
    async (groupId: string, nextPage: number) => {
      if (isLoading) return;

      const requestScope = scopeKey;
      const payload = {
        ...requestPayload,
        action: 'load_group_items',
        group_id: groupId,
        page: nextPage,
      };

      setIsLoading(true);

      try {
        const response = await GetInventoryList(token, payload);

        if (activeScopeRef.current !== requestScope) {
          return;
        }

        const apiData = (response?.data?.data?.data ?? response?.data?.data) as
          LoadGroupItemsResponse | undefined;

        if (!apiData) return;

        const resolvedGroupId = apiData.groupId ?? apiData.group_id;
        if (!resolvedGroupId) return;

        setInventoryResponse((prev) => {
          if (activeScopeRef.current !== requestScope) return prev;
          if (prev.mode !== 'group') return prev;
          if (resolvedGroupId !== groupId) return prev;

          return {
            ...prev,
            groups: prev.groups.map((group) => {
              if (group.id !== groupId) return group;

              return {
                ...group,
                items: dedupeItems([
                  ...group.items,
                  ...(apiData.items ?? [])
                    .filter((item) =>
                      shouldIncludeItemForView(item, inventoryView)
                    )
                    .map((item) => mapInventoryItem(item, inventoryView)),
                ]),
                pagination: mapPagination(apiData.pagination),
              };
            }),
          };
        });
      } catch (error) {
        console.error('Failed to load more group items:', error);
      } finally {
        if (activeScopeRef.current === requestScope) {
          setIsLoading(false);
        }
      }
    },
    [inventoryView, isLoading, requestPayload, scopeKey, token]
  );

  const handleLoadMoreSubgroupItems = useCallback(
    async (groupId: string, subgroupId: string, nextPage: number) => {
      if (isLoading) return;

      const requestScope = scopeKey;
      const payload = {
        ...requestPayload,
        action: 'load_subgroup_items',
        group_id: groupId,
        subgroup_id: subgroupId,
        page: nextPage,
        page_size: ITEM_PAGE_SIZE,
      };

      setIsLoading(true);

      try {
        const response = await GetInventoryList(token, payload);

        if (activeScopeRef.current !== requestScope) {
          return;
        }

        const apiData = response?.data?.data?.data as
          LoadSubgroupItemsResponse | undefined;

        if (!apiData) return;

        setInventoryResponse((prev) => {
          if (activeScopeRef.current !== requestScope) return prev;
          if (prev.mode !== 'group_subgroup') return prev;

          const resolvedGroupId = apiData.groupId ?? apiData.group_id;
          const resolvedSubgroupId = apiData.subgroupId ?? apiData.subgroup_id;

          if (
            resolvedGroupId !== groupId ||
            resolvedSubgroupId !== subgroupId
          ) {
            return prev;
          }

          return {
            ...prev,
            groups: prev.groups.map((group) => {
              if (group.id !== groupId) return group;

              return {
                ...group,
                subgroups: group.subgroups.map((subgroup) => {
                  if (subgroup.id !== subgroupId) return subgroup;

                  return {
                    ...subgroup,
                    items: dedupeItems([
                      ...subgroup.items,
                      ...(apiData.items ?? [])
                        .filter((item) =>
                          shouldIncludeItemForView(item, inventoryView)
                        )
                        .map((item) => mapInventoryItem(item, inventoryView)),
                    ]),
                    pagination: mapPagination(apiData.pagination),
                  };
                }),
              };
            }),
          };
        });
      } catch (error) {
        console.error('Failed to load more subgroup items:', error);
      } finally {
        if (activeScopeRef.current === requestScope) {
          setIsLoading(false);
        }
      }
    },
    [inventoryView, isLoading, requestPayload, scopeKey, token]
  );

  const isFlatMode = inventoryResponse.mode === 'flat';
  const totalRangeStart = totalCount === 0 ? 0 : isFlatMode ? 1 : 1;
  const totalRangeEnd = totalCount === 0 ? 0 : totalCount;
  const deviceSummary = inventoryResponse.summary ?? {
    total_devices: 0,
    unused: 0,
    activated: 0,
    returned: 0,
  };

  const [drawerStates, setDrawerState] = useState({
    isOpen: false,
    name: '',
  });

  const handleEditInventory = (
    isOpen?: boolean,
    name?: string,
    id?: string,
    isDuplicate?: boolean
  ) => {
    setEditID({
      id,
      isDuplicate,
    });

    setDrawerState({
      isOpen: !!isOpen,
      name: isOpen ? (name ?? '') : '',
    });

    setShowDrawer(!!isOpen);
    setShowDetailDrawer(false);
    setDeepLinkedInventoryId(null);
  };

  return (
    <>
      <div className='inventory-page-wrapper'>
        <InventoryHeader
          title={title}
          isManualRefreshing={isManualRefreshing}
          freshnessText={freshnessText}
          onRefresh={handleManualRefresh}
        />

        <InventoryToolbar
          totalRangeStart={totalRangeStart}
          totalRangeEnd={totalRangeEnd}
          totalCount={totalCount}
          isSearchVisible={isSearchVisible}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setIsSearchVisible={setIsSearchVisible}
          setPage={setPage}
          handleSearchSubmit={handleSearchSubmit}
          handleSearchIconClick={handleSearchIconClick}
          handleCloseSearch={handleCloseSearch}
          groupBy={groupBy}
          subGroupBy={subGroupBy}
          setGroupBy={setGroupBy}
          setSubGroupBy={setSubGroupBy}
          dropdownMaxHeight={dropdownMaxHeight}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          filterArray={filterArray}
          setFilterArray={setFilterArray}
          setFiltersPayload={setFiltersPayload}
          page={page}
          pageSize={PAGE_SIZE}
          inventoryView={inventoryView}
          sortOptions={sortOptions}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onRunSearch={handleRunSearchFromPanel}
          onAddInventory={() => handleEditInventory(true, 'inventory_form')}
          onSortChange={handleSortChange}
          onInventoryViewChange={handleInventoryViewChange}
          onDownload={handleDownloadInventory}
          isDownloading={isDownloading}
        />

        {isPerDeviceView ? (
          <div className='inventory-summary-cards'>
            <InventorySummaryCards summary={deviceSummary} />
          </div>
        ) : null}

        <div className='h-full'>
          <InventoryTable
            response={inventoryResponse}
            inventoryView={inventoryView}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            isLoading={isLoading}
            handleEditInventory={handleEditInventory}
            showDetailDrawer={showDetailDrawer}
            setShowDetailDrawer={setShowDetailDrawer}
            deepLinkedInventoryId={deepLinkedInventoryId}
            onDeepLinkedInventoryLoaded={clearInventoryDeepLinkFromUrl}
            onCloseDetailDrawer={() => setDeepLinkedInventoryId(null)}
            onDeleteSuccess={handleDeleteSuccess}
            onLoadMoreSubgroups={handleLoadMoreSubgroups}
            onLoadMoreGroups={handleLoadMoreGroups}
            onLoadMoreGroupItems={handleLoadMoreGroupItems}
            onLoadMoreSubgroupItems={handleLoadMoreSubgroupItems}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
        </div>
      </div>

      <InventoryDrawers
        drawerState={drawerStates}
        editID={editID}
        productsRefreshKey={productsRefreshKey}
        onClose={() => handleEditInventory(false)}
        onInventorySuccess={handleInventoryFormSuccess}
        onProductsRefresh={() => setProductsRefreshKey((prev) => prev + 1)}
      />
    </>
  );
};

export default InventoryPage;
